import { useEffect, useRef } from "react";
import { LEADS, sampleLead } from "../lib/ekg";
import { drawGrid, drawTrace, setupCanvas, SAMPLE_STEP } from "./MonitorStrip";

const CELL_WINDOW = 2.4; // seconds visible per lead cell
const STRIP_LEAD = "II";

export function TwelveLeadGrid({ params, onBeat, highlight = [] }) {
  const canvasRef = useRef(null);
  const highlightRef = useRef(highlight);
  highlightRef.current = highlight;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !params) return;
    const { ctx, cleanup } = setupCanvas(canvas);
    const buffers = LEADS.map(() => []);
    const strip = [];
    let t = 0, lastBeat = 0, lastY = 0, prevY = 0;
    let lastReal = performance.now();
    let raf = 0;
    const cellPts = Math.ceil(CELL_WINDOW / SAMPLE_STEP);

    const draw = (now) => {
      const { width: w, height: h } = canvas.getBoundingClientRect();
      const dt = Math.min(0.1, (now - lastReal) / 1000);
      lastReal = now;
      t += dt;
      const toAdd = Math.max(1, Math.floor(dt / SAMPLE_STEP));
      for (let i = 0; i < toAdd; i++) {
        const tt = t - (toAdd - i) * SAMPLE_STEP;
        LEADS.forEach((lead, li) => buffers[li].push(sampleLead(tt, params, lead)));
        const y = sampleLead(tt, params, STRIP_LEAD);
        strip.push(y);
        if (y < lastY && lastY > prevY && lastY > 0.55 && tt - lastBeat > 0.2) {
          lastBeat = tt;
          onBeat?.();
        }
        prevY = lastY;
        lastY = y;
      }
      buffers.forEach((b) => { if (b.length > cellPts) b.splice(0, b.length - cellPts); });
      const stripMax = Math.ceil(w / 1.2);
      if (strip.length > stripMax) strip.splice(0, strip.length - stripMax);

      const stripH = Math.round(h * 0.22);
      const cols = 4, rows = 3;
      const cellW = w / cols;
      const cellH = (h - stripH) / rows;

      ctx.fillStyle = "hsl(240 10% 4%)";
      ctx.fillRect(0, 0, w, h);
      drawGrid(ctx, 0, 0, w, h, 16);

      const hl = highlightRef.current;
      LEADS.forEach((lead, li) => {
        const cx = (li % cols) * cellW;
        const cy = Math.floor(li / cols) * cellH;
        const isHl = hl.includes(lead);
        ctx.strokeStyle = isHl ? "hsl(0 84% 60% / 0.9)" : "hsl(142 71% 45% / 0.25)";
        ctx.lineWidth = isHl ? 2 : 1;
        ctx.strokeRect(cx + 0.5, cy + 0.5, cellW - 1, cellH - 1);
        if (isHl) {
          ctx.fillStyle = "hsl(0 84% 60% / 0.08)";
          ctx.fillRect(cx, cy, cellW, cellH);
        }
        ctx.fillStyle = isHl ? "hsl(0 84% 70%)" : "hsl(142 71% 65%)";
        ctx.font = "bold 11px 'JetBrains Mono', monospace";
        ctx.fillText(lead, cx + 8, cy + 16);
        drawTrace(ctx, buffers[li], cx + 4, cy, cellW - 8, cellH, 0.26, 4);
      });

      const sy = h - stripH;
      ctx.strokeStyle = "hsl(142 71% 45% / 0.4)";
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, sy + 0.5); ctx.lineTo(w, sy + 0.5); ctx.stroke();
      ctx.fillStyle = "hsl(142 71% 65%)";
      ctx.font = "bold 11px 'JetBrains Mono', monospace";
      ctx.fillText(`${STRIP_LEAD}  rhythm strip`, 8, sy + 16);
      drawTrace(ctx, strip, 0, sy, w, stripH, 0.28, 8);

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); cleanup(); };
  }, [params, onBeat]);

  return <canvas ref={canvasRef} data-testid="twelve-lead-grid" className="w-full h-[420px] sm:h-[480px] block" />;
}
